import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import appNoticeService from '../services/appNoticeService';
import { useAuth } from './AuthContext';

/**
 * Owns "what, if anything, should interrupt this person right now".
 * docs/plans/2026-08-handoff-layout/09-app-notices.md
 *
 * One fetch, two consumers: AppNoticeHost renders the modal / bottom sheet, and
 * PageShell renders the inline banner for the downgraded resume prompt (after
 * three dismissals it stops interrupting but keeps asking quietly). Both read
 * the same notice so the endpoint — which stamps "last shown" server-side — is
 * only ever called once per check.
 */
const AppNoticeContext = createContext(null);

const FOREGROUND_GAP_MS = 30 * 60 * 1000;

function lastLocation() {
  try {
    const raw = localStorage.getItem('pr.userLocation');
    const loc = raw ? JSON.parse(raw) : null;
    return loc && Number.isFinite(loc.lat) && Number.isFinite(loc.lng) ? loc : null;
  } catch { return null; }
}

// Routes where an interruption is never acceptable.
function contextFor(pathname) {
  if (/^\/(events|e)\/[^/]+/.test(pathname) || pathname.startsWith('/recover-payment') || pathname.startsWith('/ticket')) return 'checkout';
  if (pathname.startsWith('/portals') || pathname.startsWith('/signals')) return 'signal';
  return null;
}

export function AppNoticeProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  const [feed, setFeed] = useState({ interrupting: null, banner: null });
  const lastCheck = useRef(0);

  const check = useCallback(async () => {
    if (!isAuthenticated) { setFeed({ interrupting: null, banner: null }); return; }
    lastCheck.current = Date.now();
    const loc = lastLocation();
    const r = await appNoticeService.next(contextFor(pathname), loc?.lat ?? null, loc?.lng ?? null);
    if (r.success && r.data) {
      setFeed({ interrupting: r.data.interrupting || null, banner: r.data.banner || null });
    }
  }, [isAuthenticated, pathname]);

  // Cold start.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { check(); }, [isAuthenticated]);

  // Foreground after a long gap.
  useEffect(() => {
    function onVisible() {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastCheck.current < FOREGROUND_GAP_MS) return;
      check();
    }
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [check]);

  /** Dismiss the interrupting notice: a strike for the resume sheet, a 7-day
   *  cool-off for a manual one. */
  const dismiss = useCallback(async () => {
    const notice = feed.interrupting;
    if (!notice) return;
    if (notice.kind === 'resume') await appNoticeService.dismissResumePrompt();
    else if (notice.id) await appNoticeService.dismiss(notice.id);
    setFeed((f) => ({ ...f, interrupting: null }));
  }, [feed.interrupting]);

  const act = useCallback(async () => {
    const notice = feed.interrupting;
    if (!notice) return '/';
    if (notice.kind === 'manual' && notice.id) await appNoticeService.act(notice.id);
    setFeed((f) => ({ ...f, interrupting: null }));
    return notice.primaryHref;
  }, [feed.interrupting]);

  /** The banner's × — quiet for a day, no strike. */
  const hideBanner = useCallback(async () => {
    await appNoticeService.hideResumeBanner();
    setFeed((f) => ({ ...f, banner: null }));
  }, []);

  /** The banner's CTA — same destination, and it stops showing once finished. */
  const openBanner = useCallback(() => {
    const href = feed.banner?.primaryHref || '/portal/edit';
    setFeed((f) => ({ ...f, banner: null }));
    return href;
  }, [feed.banner]);

  const value = useMemo(() => ({
    // The modal / bottom sheet.
    interrupting: feed.interrupting,
    // The persistent strip: a resume prompt dismissed three times. Independent of
    // the above, so both can be present at once.
    inlineResume: feed.banner,
    dismiss,
    act,
    hideBanner,
    openBanner,
    refresh: check,
  }), [feed, dismiss, act, hideBanner, openBanner, check]);

  return <AppNoticeContext.Provider value={value}>{children}</AppNoticeContext.Provider>;
}

/** Safe outside the provider (unauthenticated shells) — returns an empty state. */
export function useAppNotice() {
  return useContext(AppNoticeContext) || {
    interrupting: null,
    inlineResume: null,
    dismiss: async () => {},
    act: async () => '/',
    hideBanner: async () => {},
    openBanner: () => '/',
    refresh: async () => {},
  };
}

export default AppNoticeContext;
